<template>
  <div class="station-view" @click="focusBarcodeEntry()">
    <div class="station-main-general-view">
      <div class="row">
        <div class="col-6">
          <div class="station-scanner-entry">
            <h2 class="text-muted">Enter panel barcode to scan a panel for {{productionStage.name}}</h2>
            <div class="row">
              <div class="col-10">
                <FormInputText ref="panelBarcodeScan" v-model="barcodeEntry" placeholder="Barcode" @enter="onScannedBarcode()" prependSlot>
                  <template v-slot:prepend>
                    <i class="far fa-scanner"></i>
                  </template>
                </FormInputText>
              </div>
              <div class="col-2">
                <StationControlPanelButton class="btn-default" sizeClass="xl" iconStyle="fal" icon="fa-scanner-keyboard" @click="scanOptionsClicked">Scan Options</StationControlPanelButton>
              </div>
            </div>
          </div>
        </div>
        <div class="col-6">
          <StationAlertMessage ref="alertMessage" />
        </div>
      </div>

      <h2>{{productionStation.name}} Panel Queue</h2>
      <div class="station-list-container">
        <div ref="queueList" class="station-list station-thumb-layout" v-if="!isStationQueueEditorView">
        
          <QueuePanelThumbItem
            v-for="panel of productionStation.productionPanels"
            :key="panel.id"
            :panel="panel"
            :highlightColour="productionStage.highlightColour"
            :currentStationId="productionStation.id"
            :queueDisplayType="productionStation.queueDisplayType"
            :ref="`panel-queue-${panel.id}`"
            @previewClicked="queuePanelPreviewClicked($event, panel)"
            @panelClicked="queuePanelClicked($event, panel)"
            @removePanel="removePanelFromStation(panel.id)">
          </QueuePanelThumbItem>
        
          <h2 class="text-muted text-center" v-if="productionStation.productionPanels.length == 0 && stationCanScanIn">No panels at this station</h2>
          <h2 class="text-muted text-center" v-if="productionStation.productionPanels.length == 0 && !stationCanScanIn">No panels at stations visible to this station</h2>
        </div>

        <QueueEditorViewContainer
          v-if="isStationQueueEditorView"
          :currentStationId="productionStation.id"
          :productionPanels="productionStation.productionPanels"

          @panelClick="$emit('panelClick', $event)"
          @removePanel="removePanelFromStation($event)"
        />
      </div>
    </div>

  </div>
</template>

<style scoped>
.station-main-general-view {
  flex: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
</style>

<script>
import _ from 'lodash'
import formMixin from '@/mixins/form'
import Utils from '@/utils/utils'
import EventBus from '@/utils/eventBus'

import ProductionStationMixin from '@/apps/glass-production-station/src/mixins/productionStation'
import QueuePanelListItem from '@/apps/glass-production-station/src/components/station/queuePanelListItem'
import QueuePanelThumbItem from '@/apps/glass-production-station/src/components/station/queuePanelThumbItem'
import QueueEditorViewContainer from '@/apps/glass-production-station/src/components/station/queueEditorViewContainer'
import StationAlertMessage from '@/apps/glass-production-station/src/components/station/stationAlertMessage'
import FastForwardCompleteDialog from '@/apps/glass-production-station/src/views/fastForwardCompleteDialog'
import StationControlPanelButton from '@/apps/glass-production-station/src/components/buttons/stationControlPanelButton'
import StationScanWarningDialog from '@/apps/glass-production-station/src/views/stationScanWarningDialog'

import { ProductionStationQueueDisplayTypes } from '@/store/types'


export default {
  name: 'GeneralStationView',
  mixins: [formMixin, ProductionStationMixin],
  components: {
    StationAlertMessage,
    QueuePanelListItem,
    QueuePanelThumbItem,
    QueueEditorViewContainer,
    StationControlPanelButton
  },
  props: {
    productionStage: Object,
    productionStation: Object
  },
  data() {
    return {
      barcodeEntry: '',
      panelScanSuccess: false,
      panelScanError: false,
      panelDetails: {
        barcode: '',
        nextStage: '',
        nextStageColour: '#400000',
        tasksComplete: false,
        despatchType: '',
        panelTaskStarted: false,
        panelTaskCompleted: false,
      },
      panelScanErrorMessage: ''
    }
  },
  computed: {
    stationHasSecondaryStation() {
      return this.productionStation.secondaryStationId != null
    },
    secondaryStationName() {
      if(this.productionStation.secondaryStation)
        return this.productionStation.secondaryStation.name
      return ''
    },
    stationCanScanIn() {
      if(this.productionStation)
        return this.productionStation.isScanInAvailable
      return false
    },
    stationCanScanOut() {
      if(this.productionStation)
        return this.productionStation.isScanOutAvailable
      return false
    },
    isStationQueueEditorView() {
      if(this.productionStation)
        return this.productionStation.queueDisplayType == ProductionStationQueueDisplayTypes.editorView
      return false
    }
  },
  watch: {
    'productionStation.productionPanels': {
      handler: function(newVal, oldVal) {
        // Only scroll down if new panels added
        if(newVal && oldVal) {
          if(newVal.length > oldVal.length) {
            this.$nextTick(() => {
              this.queueListUpdated()
            })
          }
        }
      }
    }
  },
  mounted() {
    this.$refs.panelBarcodeScan.select()
    this.$refs.panelBarcodeScan.focus()

    EventBus.on(EventBus.EventTypes.panelLabelPrinted, this.focusBarcodeEntry)
  },
  beforeDestroy() {
    EventBus.off(EventBus.EventTypes.panelLabelPrinted, this.focusBarcodeEntry)
  },
  methods: {
    onScannedBarcode(options = {}) {
      if(this.barcodeEntry.trim().length == '') {
        this.barcodeEntry = ''
        return
      }

      var scanData = {
        barcode: this.barcodeEntry
      }
      if(options)
        scanData = _.merge(scanData, options)

      // Run the scan in/out function on the panel scanned
      this.$apiService.glassProductionStationService.runPanelBarcodeScan(this.productionStation.id, scanData)
      .then(response => {
        this.$refs.alertMessage.hide()

        let barcode = response.data.panelBarcode
        var nextStage = ''

        if(response.data.panelTaskCompleted) {
          if(response.data.nextStage && response.data.nextStage.productionStage) {
            nextStage = response.data.nextStage.productionStage.name
          }
        }

        if(response.data.tasksComplete) {
          var despatchName = response.data.despatchTaskType ? response.data.despatchTaskType.name : ''
          this.$refs.alertMessage.displayAlert('success', 'Panel Completed', `Panel ${barcode} has finished processing and is due at <strong>despatch</strong> for <strong>${despatchName}</strong>`)
        } else {
          if(response.data.panelTaskStarted)
            this.$refs.alertMessage.displayAlert('success', 'Panel Scanned In', `Panel ${barcode} added`)
          else if(response.data.panelTaskCompleted)
            this.$refs.alertMessage.displayAlert('success', 'Panel Scanned Out', `Panel ${barcode} is next due at <strong>${nextStage}</strong>`)
        }

        // Back to scanning
        this.barcodeEntry = ''
        this.$refs.panelBarcodeScan.focus()
      })
      .catch(err => {
        let errorInfo = this.processFormResponse(err)
        
        this.$refs.alertMessage.hide()

        // If the server gives fast forward options, display the fast forward dialog
        if(errorInfo.errorData && errorInfo.errorData.fastForwardOptions && errorInfo.errorData.fastForwardOptions.available) {

          // Show fast forward complete options
          this.displayModal(FastForwardCompleteDialog, { 
            productionStageId: this.productionStage.id,
            productionStationId: this.productionStation.id,
            fastForwardOptions: errorInfo.errorData.fastForwardOptions,
          })
          .then((result) => {

            if(result.allTasksCompleted) {
              // Attempt to automatically scan in the panel again now we have completed all pending tasks
              this.$nextTick(() => {
                this.onScannedBarcode()
              })
              return
            } else {
              this.$refs.alertMessage.displayAlert('success', 'Tasks Completed', 'Pending Tasks completed, ready for scanning')
            }

            // Back to scanning
            this.barcodeEntry = ''
            this.$refs.panelBarcodeScan.focus()
          })          
          return
        }

        // If the server gives warnings + override, display the override warning dialog
        if(errorInfo.errorData && errorInfo.errorData.overrideWarningOptions && errorInfo.errorData.overrideWarningOptions.available) {

          // Show warning override options
          this.displayModal(StationScanWarningDialog, { 
            productionStageId: this.productionStage.id,
            productionStationId: this.productionStation.id,
            overrideWarningOptions: errorInfo.errorData.overrideWarningOptions,
          })
          .then((result) => {

            if(result) {
              // Attempt to scan again with override specified
              this.$nextTick(() => {
                this.onScannedBarcode({ overrideWarnings: true, overrideName: result.name })
              })
            }
          })          
          return
        }

        this.$refs.alertMessage.displayAlert('error', 'Scanning Error!', errorInfo.errorMessage)

        // Back to scanning
        this.barcodeEntry = ''
        this.$refs.panelBarcodeScan.focus()
      })

    },
    removePanelFromStation(productionPanelId) {
      // Remove panel
      this.$apiService.glassProductionStationService.removePanelFromStation(this.productionStation.id, { productionPanelId: productionPanelId })
      .then(response => {
        this.$refs.alertMessage.hide()

        this.$refs.panelBarcodeScan.select()
      })
      .catch(err => {
        let errorInfo = this.processFormResponse(err)
        this.$refs.alertMessage.displayAlert('error', 'Error Removing Panel!', errorInfo.errorMessage)

        // Back to scanning
        this.barcodeEntry = ''
        this.$refs.panelBarcodeScan.focus()
      })
    },
    queuePanelClicked(event, panel) {
      let panelComponent = this.$refs[`panel-queue-${panel.id}`][0]

      this.$emit('panelClick', { panel: panel, target: panelComponent.$el, forceElement: true })
    },
    queuePanelPreviewClicked(event, panel) {
      let panelComponent = this.$refs[`panel-queue-${panel.id}`][0]

      this.$emit('panelPreviewClick', { panel: panel, target: panelComponent.$el, forceElement: true })
    },
    focusBarcodeEntry() {
      // Ignore if a modal dialog if open
      if(this.isDocumentModalVisible())
        return

      this.$refs.panelBarcodeScan.select()
    },
    queueListUpdated() {
      // Scroll to the last panel scanned
      if(this.isStationQueueEditorView && this.$refs.queueList) {
        this.$refs.queueList.scrollTop = this.$refs.queueList.scrollHeight
      }
    },
    scanOptionsClicked() {
      this.displayStationScanOptions(this.productionStation.id)
    }
  }
  
}
</script>